<script setup lang="ts">
import 'vue-multiselect/dist/vue-multiselect.min.css'
import '@/assets/multiselect.css'
import Multiselect from 'vue-multiselect'
import { computed, defineModel, defineProps } from 'vue'

interface GroupedOption {
  label: string
  options: { id: string; value: string; label: string }[]
}

const props = defineProps({
  options: {
    type: Array as () => GroupedOption[],
    required: true,
  },
  label: {
    type: String,
    required: true,
  },
  required: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: '',
  },
})

const modelValue = defineModel<string | null>()

const flatOptions = computed(() => {
  const out: { id: string; value: string; label: string }[] = []
  for (const g of props.options) {
    out.push(...g.options)
  }
  return out
})

const selectedOption = computed(() => {
  if (modelValue.value === null || modelValue.value === undefined) return null
  return flatOptions.value.find((opt) => opt.value === modelValue.value) || null
})

function handleModelUpdate(val: { id: string; value: string; label: string } | null) {
  modelValue.value = val ? val.value : null
}
</script>

<template>
  <label class="form-control w-full">
    <div class="label" v-if="label">
      <span class="label-text font-semibold">
        {{ label }}
        <span v-if="required" class="text-red-500">*</span>
      </span>
    </div>

    <div class="select-grouped-daisy w-full">
      <Multiselect
      :model-value="selectedOption"
      @update:model-value="handleModelUpdate"
      :options="options"
      :group-values="'options'"
      :group-label="'label'"
      :group-select="false"
      :required="required"
      :searchable="true"
      :close-on-select="true"
      :placeholder="placeholder || 'Sélectionner ' + label + '...'"
      select-label="Sélectionner"
      selected-label="Sélectionné"
      deselect-label="Retirer"
      label="label"
      track-by="value"
      :disabled="disabled"
    >
      <template #noOptions>
        <div class="p-2">Aucune option disponible</div>
      </template>
      <template #noResult>
        <div class="p-2">Aucun résultat trouvé</div>
      </template>
    </Multiselect>
    </div>
  </label>
</template>

<style scoped>
.multiselect {
  position: relative;
  min-height: 2rem;
}
.multiselect--active {
  z-index: 9999 !important;
}
.multiselect--active :deep(.multiselect__content-wrapper) {
  z-index: 9999 !important;
}
.multiselect :deep(.multiselect__tags) {
  width: 100%;
  min-height: 0;
  padding: 0.25rem 0.5rem;
  border: 1px solid var(--color-base-300);
  background-color: var(--color-base-100);
  color: var(--color-base-content);
  border-radius: var(--radius-field, 0.5rem);
}
.multiselect:focus-within :deep(.multiselect__tags) {
  outline: none;
  box-shadow: 0 0 0 2px var(--color-base-100), 0 0 0 4px var(--color-primary);
}
.multiselect :deep(.multiselect__input),
.multiselect :deep(.multiselect__single) {
  background-color: var(--color-base-100);
  color: var(--color-base-content);
}
.multiselect :deep(.multiselect__content-wrapper) {
  background-color: var(--color-base-100);
  border: 1px solid var(--color-base-300);
  border-radius: var(--radius-field, 0.5rem);
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
}
.multiselect :deep(.multiselect__option--highlight) {
  background-color: var(--color-primary) !important;
  color: var(--color-primary-content) !important;
}
.multiselect :deep(.multiselect__option--group) {
  font-weight: 600;
  color: var(--color-base-content);
  background-color: var(--color-base-200);
}
.multiselect :deep(.multiselect__option--group .multiselect__option) {
  padding-left: 1rem;
}
.multiselect :deep(.multiselect__tag) {
  background-color: var(--color-primary);
  color: var(--color-primary-content);
  padding: 0.125rem 0.5rem;
  border-radius: var(--radius-field, 0.5rem);
}
.multiselect :deep(.multiselect__tag-icon:after) {
  color: var(--color-primary-content);
}
.multiselect :deep(.multiselect__tag-icon:hover) {
  opacity: 0.9;
}
.multiselect.multiselect--disabled :deep(.multiselect__tags) {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
